import React, { useState } from 'react';

import { Button } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { Textarea } from '@/components/ui/textarea';
import { keys } from 'components/DisasterGPT/hooks/useSitrepsV2';
import { usePublishAorReport } from 'components/Reports/hooks/useAorReportApprovalPolicy';
import { useApproveRequest, useRejectRequest } from 'hooks/useApprovals';
import { ITEM_TYPES } from 'constants/itemType';
import type { GetSitrepsResponse } from 'components/Reports/reports.types';
import { toast } from 'react-toastify';

type ReviewCardActionsProps = {
  sitrep: GetSitrepsResponse;
  isEditMode?: boolean;
};

/** Approve/Reject buttons with optional comment, rendered inside a review card. */
const ReviewCardActions: React.FC<ReviewCardActionsProps> = ({
  sitrep,
  isEditMode = false,
}) => {
  const [reviewComment, setReviewComment] = useState('');
  const approve = useApproveRequest(keys.base);
  const reject = useRejectRequest(keys.base);
  const publishReport = usePublishAorReport();
  const reviewCommentId = `review-comment-${sitrep.item_id}`;
  const publishesDirectly = !!sitrep.incident_id;

  const isBusy = approve.isPending || reject.isPending || publishReport.isPending;

  const handlePublish = async () => {
    try {
      await publishReport.mutateAsync(sitrep.id);
      toast.success('Published as the current report');
    } catch (error) {
      const response = (
        error as { response?: { status?: number; data?: { error?: unknown } } }
      ).response;
      const serverMessage = response?.data?.error;
      toast.error(
        typeof serverMessage === 'string'
          ? serverMessage
          : 'Failed to publish report'
      );
    }
  };

  if (publishesDirectly) {
    return (
      <div className="reports-card__reviews tw:mt-3 tw:flex tw:justify-end">
        <Button
          type="button"
          disabled={isBusy || isEditMode}
          onClick={() => {
            void handlePublish();
          }}
        >
          Publish
        </Button>
      </div>
    );
  }

  return (
    <div className="reports-card__reviews tw:mt-3 tw:flex tw:flex-col tw:gap-2">
      <Field>
        <FieldLabel htmlFor={reviewCommentId}>
          Add a comment to your review (optional)
        </FieldLabel>
        <Textarea
          id={reviewCommentId}
          value={reviewComment}
          onChange={(e) => setReviewComment(e.target.value)}
          maxLength={1000}
          rows={2}
        />
      </Field>
      <div className="tw:flex tw:flex-wrap tw:justify-end tw:gap-2">
        <Button
          variant="destructive"
          type="button"
          disabled={isBusy || isEditMode}
          onClick={() => {
            reject.mutate({
              itemType: ITEM_TYPES.Sitrep,
              itemId: sitrep.item_id,
              groupId: sitrep.group_guid,
              incidentId: sitrep.incident_id ?? undefined,
              comments: reviewComment,
            });
          }}
        >
          Reject
        </Button>
        <Button
          type="button"
          disabled={isBusy || isEditMode}
          onClick={() => {
            approve.mutate({
              itemType: ITEM_TYPES.Sitrep,
              itemId: sitrep.item_id,
              groupId: sitrep.group_guid,
              incidentId: sitrep.incident_id ?? undefined,
              comments: reviewComment,
            });
          }}
        >
          Approve
        </Button>
      </div>
    </div>
  );
};

export default ReviewCardActions;
